import {
  useFilters,
  useGetPrecedingFilters,
} from 'features/drilldown/data/DrilldownFilterContext'
import { useVersionAwareDrilldownPanelData } from 'features/drilldown/data/DrilldownPanelDataContext'
import { toRolodexFilter } from 'features/drilldown/panels/global/filters/DrilldownFilterView'
import {
  EnrichedOntologyFieldDefinition,
  OntologyValueType,
  useFieldValues,
  useOntology,
} from 'features/dynamicOntology'
import { useMemo } from 'react'
import { RolodexHistogramFilter } from 'types'

import { useSecondaryValues } from './useSecondaryValues'
import {
  matchTermsToValues,
  normalizeTerm,
  parsePastedTerms,
  TermMatchReport,
} from './valueListFilterMatching'
import { ValueListFilterPanelData } from './valueListFilterPanelData'

export interface ValueListFilterState {
  report: TermMatchReport
  fieldValueCount: number
  isLoadingFieldValues: boolean
  matchFieldDef?: EnrichedOntologyFieldDefinition
  secondaryValuesByValue: Readonly<Record<string, string>>
}

function toFilterValues(
  values: ReadonlyArray<string>,
  fieldDef: EnrichedOntologyFieldDefinition
): OntologyValueType {
  return fieldDef.type === 'number' ? values.map(Number) : [...values]
}

/**
 * Matches the panel's pasted terms against the values of the selected field
 * and applies the result as an in/not-in filter on the exploration.
 */
export function useValueListFilter(): ValueListFilterState {
  const { panelData } =
    useVersionAwareDrilldownPanelData<ValueListFilterPanelData>()
  const {
    matchField,
    secondaryField,
    matchMode,
    pastedText,
    chosenValuesByTerm,
    shouldSuggestFuzzyMatches,
  } = panelData

  const { fields } = useOntology()
  const matchFieldDef: EnrichedOntologyFieldDefinition | undefined =
    fields[matchField]

  const precedingFilters = useGetPrecedingFilters()
  const { values, isLoading } = useFieldValues(matchField, {
    filters: precedingFilters,
  })
  const secondaryValuesByValue = useSecondaryValues(
    matchFieldDef,
    fields[secondaryField],
    precedingFilters
  )

  // Histogram values arrive untyped, so numeric fields would break normalizing.
  const fieldValues = useMemo(() => values.map(String), [values])

  const knownNormalizedValues = useMemo(
    () => new Set(fieldValues.map(normalizeTerm)),
    [fieldValues]
  )

  const terms = useMemo(
    () => parsePastedTerms(pastedText, knownNormalizedValues),
    [knownNormalizedValues, pastedText]
  )

  const report = useMemo(
    () =>
      matchTermsToValues({
        terms,
        fieldValues,
        chosenValuesByTerm,
        shouldSuggestFuzzyMatches,
      }),
    [chosenValuesByTerm, fieldValues, shouldSuggestFuzzyMatches, terms]
  )

  const listFilters = useMemo<
    ReadonlyArray<RolodexHistogramFilter> | undefined
  >(() => {
    if (!matchFieldDef || !report.selectedValues.length) {
      return undefined
    }
    const filter = toRolodexFilter({
      field: matchField,
      fieldDef: matchFieldDef,
      operator: matchMode === 'include' ? 'in' : 'notin',
      value: toFilterValues(report.selectedValues, matchFieldDef),
    })
    return filter ? [filter] : undefined
  }, [matchField, matchFieldDef, matchMode, report.selectedValues])

  useFilters(listFilters)

  return {
    report,
    fieldValueCount: fieldValues.length,
    isLoadingFieldValues: isLoading,
    matchFieldDef,
    secondaryValuesByValue,
  }
}
